import {
  Form,
  Modal,
  Space,
  Spin,
} from "antd"
import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useStore } from "stores/utils/use-store"
import { NotificationsPerGame, SpecialEvent } from "../main"
import { ValidateErrorEntity } from "rc-field-form/lib/interface"
import { observer } from "mobx-react"
import { SpecialEventEditHeader } from "./components/header"
import { v4 as uuidv4 } from "uuid"
import { notifications as appNotifications } from "notifications"
import Styles from "./styles.module.css"
import { MainInfoEditor } from "./components/mainInfoEditor"
import { GamesEditor } from "./components/gamesEditor"
import { gameFieldsFromEvent, gamesForSave } from "domains/specialEvents/timeBasedGames"
import { ConfigItem, SpecialEventsConfigType } from "domains/managingJson/store/types"
import {
  convertUtcToZonedWallTime,
  convertZonedWallTimeToUtc,
  DEFAULT_SPECIAL_EVENT_TIME_ZONE,
} from "./utils/timeZone"
import { normalizeSpecialEvent, prepareNotificationsPerGameForSave } from "domains/specialEvents/normalization"
import {
  classifyStoredTargeting,
  describeTargetingSupport,
  getGameLabel,
  getTargetingWarnings,
  isDraftFor,
  isTargetingParseable,
  listWidenedGameIds,
  parseTargeting,
  restoreDroppedTargeting,
  stringifyTargeting,
} from "domains/specialEvents/targeting"

const DEFAULT_EVENT_WEIGHT = 1

type GameConfig = Pick<NotificationsPerGame, "gameId"> & { targeting?: unknown }

// The only silent widening left: a campaign that was restricted starts serving every mode. Over-
// delivery of a mode-exclusive creative cannot be taken back once pixels have fired, so it is
// acknowledged rather than merely logged. This asks; it never refuses.
const confirmWidening = (
  widened: string[],
  // Rendered outside the React tree, so unmounting the page leaves it on screen with a live promise.
  register?: (cancel: () => void) => void,
) => new Promise<boolean>(resolve => {
  if (!widened.length) {
    resolve(true)

    return
  }

  const instance = Modal.confirm({
    title: "This campaign will start serving in every mode",
    content: `Targeting was removed for ${widened.join(", ")}. Save anyway?`,
    okText: "Save",
    cancelText: "Keep editing",
    onOk: () => resolve(true),
    onCancel: () => resolve(false),
  })

  register?.(() => {
    instance.destroy()
    resolve(false)
  })
})

// A stored campaign loads normalized, so raw stored text would always look like a change.
const normalizeForCompare = (value: unknown) => {
  try {
    return parseTargeting(value)
  } catch {
    return value
  }
}

const MAX_TOAST_WARNINGS = 3

const joinWarnings = (warnings: string[]) => {
  const shown = warnings.slice(0, MAX_TOAST_WARNINGS).join(" ")
  const rest = warnings.length - MAX_TOAST_WARNINGS

  return rest > 0 ? `${shown} (${rest} more — open Game events to see the rest.)` : shown
}

// Targeting never blocks a save. Unparseable text is dropped rather than written under a key
// the client reads as an object, so that case is an error the admin cannot miss, not an info toast.
const describeKept = (game: string, kept: unknown) => {
  const kind = classifyStoredTargeting(kept)

  if (kind === "rules") return `The targeting saved earlier is still in force for ${game} and was not changed.`
  if (kind === "invalid") {
    return `The targeting already stored for ${game} is not valid either, so this campaign will not serve `
      + "on that game at all until one of them is fixed."
  }

  return `${game} is not targeted, so this campaign runs in every mode there.`
}

const bannerImageOn = (config: unknown) => (
  (config as { launchGameBanner?: { image?: string } })?.launchGameBanner?.image
)

// The client applies country, region and ISP limits before the launch draw, so a neighbour that is
// restricted at all still leaves every excluded user seeing this campaign's banner.
// Only the ISP check drops blank entries; a blank country or region excludes every user.
const hasEntries = (values?: string[]) => Boolean(values?.some(value => value?.trim()))

const isUnconditionallyEligible = (event: SpecialEvent) => !event.restrictedToCountries?.length
  && !event.restrictedToRegions?.length
  && !hasEntries(event.excludedIsps)

// Containment, not overlap: on any day of this flight the neighbour does not cover, the untargeted
// tier is empty and this campaign takes the banner. A partial deferral is still over-delivery.
const coversFlight = (neighbour: SpecialEvent, campaign: SpecialEvent) => (
  Date.parse(neighbour.startDate) <= Date.parse(campaign.startDate)
  && Date.parse(campaign.endDate) <= Date.parse(neighbour.endDate)
)

// Mirrors the client's untargeted tier: the first entry matching this game by strict string
// equality, whatever art it carries. A neighbour with no launch art still empties the slot, and an
// entry whose gameId was stored as a number is missed here exactly as the client misses it.
const takesLaunchBannerOn = (event: SpecialEvent, gameId: unknown) => {
  const entry = event.notificationsPerGame?.find(candidate => candidate?.gameId === String(gameId))

  return classifyStoredTargeting(entry?.targeting) === "none" && Boolean(entry)
}

// The launch draw happens before any match facts exist, so rules cannot be evaluated there and the
// client never lets a targeted campaign win the slot — only the banner. Where no untargeted campaign
// is eligible for every user across the whole flight, this campaign's launch creative serves to
// everyone launching the game, including the modes it was sold as excluding.
const describeLaunchBanner = (
  game: string,
  gameConfig: GameConfig,
  campaign?: SpecialEvent,
  neighbours: SpecialEvent[] = [],
) => {
  if (!bannerImageOn(gameConfig)) return undefined

  const deferredBy = campaign && neighbours.find(event => coversFlight(event, campaign)
    && isUnconditionallyEligible(event)
    && takesLaunchBannerOn(event, gameConfig?.gameId))

  if (deferredBy) {
    return `"${deferredBy.name}" runs untargeted on ${game} for this whole flight with no country or ISP `
      + "limits, so it takes the launch banner and this campaign's Launch Game Banner will not serve there "
      + "(as of the campaigns loaded when this page opened, and unless a user has dismissed it). This "
      + "campaign serves from match start, in the modes its rules match."
  }

  return `No untargeted campaign covers this whole flight on ${game} for every user, so this campaign takes `
    + "the launch banner. Launch happens before any match facts exist, so the rules are not applied there: "
    + "the Launch Game Banner and its pixels serve to everyone launching the game, in every mode. It takes "
    + "the banner only — no triggers and no rewards until a match-start selection picks it on its rules."
}

// The over-delivery starts on the save that adds the creative or widens the dates, not only on the
// save that changes the rules — a campaign is usually targeted first and given its banner later.
const launchSurfaceChanged = (
  gameConfig: GameConfig,
  storedConfig: NotificationsPerGame | undefined,
  campaign: SpecialEvent | undefined,
  storedCampaign: SpecialEvent | undefined,
) => bannerImageOn(gameConfig) !== bannerImageOn(storedConfig)
  || campaign?.startDate !== storedCampaign?.startDate
  || campaign?.endDate !== storedCampaign?.endDate

const reportTargeting = (
  notificationsPerGame?: GameConfig[],
  storedCampaign?: SpecialEvent,
  neighbours: SpecialEvent[] = [],
  campaign?: SpecialEvent,
) => {
  const targetedGames: string[] = [];

  (notificationsPerGame || []).forEach(gameConfig => {
    const storedConfig = storedCampaign?.notificationsPerGame
      ?.find(config => String(config?.gameId) === String(gameConfig?.gameId))
    const keptTargeting = storedConfig?.targeting
    // A caution is advice about a key, not about this save. Repeating it whenever anything else on the
    // campaign changes is how admins learn to ignore the toast — so only raise it when it is news.
    const changed = stringifyTargeting(normalizeForCompare(gameConfig?.targeting))
      !== stringifyTargeting(normalizeForCompare(keptTargeting))
    const warnings = getTargetingWarnings(gameConfig?.targeting, gameConfig?.gameId, { includeCautions: changed })
    const game = getGameLabel(gameConfig?.gameId)

    if (isTargetingParseable(gameConfig?.targeting)) {
      const isTargeted = classifyStoredTargeting(parseTargeting(gameConfig?.targeting)) === "rules"
      if (isTargeted) targetedGames.push(game)
      const launch = isTargeted
        && (changed || launchSurfaceChanged(gameConfig, storedConfig, campaign, storedCampaign))
        ? describeLaunchBanner(game, gameConfig, campaign, neighbours)
        : undefined
      const all = [...warnings, ...(launch ? [launch] : [])]
      if (all.length) appNotifications.info(`Targeting for ${game}: ${joinWarnings(all)}`, 10000)

      return
    }

    if (!warnings.length) return

    appNotifications.error(
      `The campaign saved, but your targeting JSON for ${game} did not: ${joinWarnings(warnings)} `
      + `${describeKept(game, keptTargeting)} Your text is still in the editor.`,
      15000,
    )
  })

  // Every save, not only the save that adds targeting: what changes underneath is the installed base.
  const support = describeTargetingSupport(targetedGames)
  if (support) appNotifications.info(support, 12000)
}

// A failing field can sit in a tab the admin is not looking at, so the toast has to name it.
const describeField = (name: (string | number)[], notificationsPerGame?: GameConfig[]) => {
  const labels = name.filter((part): part is string => typeof part === "string" && part !== "notificationsPerGame")
  const gameIndex = name[0] === "notificationsPerGame" && typeof name[1] === "number" ? name[1] : undefined
  const gameLabel = gameIndex === undefined ? undefined : getGameLabel(notificationsPerGame?.[gameIndex]?.gameId)

  return [gameLabel, labels.join(" - ") || "a field"].filter(Boolean).join(" - ")
}

export const EditSpecialEventPage = observer(() => {
  const { id } = useParams<{ id: string }>()
  const { dataStore: { managingJsonStore } } = useStore()
  const [editedEvent, setEditedEvent] = useState<SpecialEvent | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()
  const [form] = Form.useForm()
  const formRef = useRef(null)
  const [gamesTab, setGamesTab] = useState("overview")
  const pendingScrollRef = useRef<(string | number)[] | undefined>(undefined)
  const draftOwnerRef = useRef<string | undefined>(undefined)
  const [importedSpecialEvent, setImportedSpecialEvent] = useState<SpecialEvent | undefined>(
    (location.state as { importedSpecialEvent?: SpecialEvent } | null)?.importedSpecialEvent,
  )
  // Held across the write so a superseded save cannot disarm a live one, and cleared once it has run.
  const ownCallbackRef = useRef<(() => Promise<void>) | undefined>(undefined)
  const cancelWideningRef = useRef<(() => void) | undefined>(undefined)
  const savingRef = useRef(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    const getData = async() => {
      await managingJsonStore.getData()
      setIsLoading(false)
    }
    getData()
  }, [managingJsonStore])

  useEffect(() => {
    if (!id) return
    if (id === "new") {
      setEditedEvent(undefined)

      return
    }
    const eventsData = managingJsonStore?.itemTypes?.filter(
      item => item.type === "specialEvents",
    ) as ConfigItem<"specialEvents">[]
    if (eventsData && eventsData.length > 0) {
      const data = eventsData[0].data
      const specialEvent = data?.specialEvents?.find(event => event.uid === id)
      setEditedEvent(specialEvent ? normalizeSpecialEvent(specialEvent) : undefined)
    }
  }, [id, managingJsonStore.itemTypes])

  useEffect(() => {
    // checking if form is rendered - if not, we won't set fields value
    // loader might be rendered instead of form while data is loading
    if (!formRef.current) return
    const initialEvent = editedEvent || importedSpecialEvent
    // A failed post-save refresh leaves editedEvent undefined; seeding then wipes the dates in a form
    // the admin is still looking at, and the next save fails validation on fields they never touched.
    if (!initialEvent && id !== "new") return
    const editedDateRange = initialEvent ? [
      convertUtcToZonedWallTime(initialEvent.startDate, DEFAULT_SPECIAL_EVENT_TIME_ZONE),
      convertUtcToZonedWallTime(initialEvent.endDate, DEFAULT_SPECIAL_EVENT_TIME_ZONE),
    ] : undefined
    const gameFields = gameFieldsFromEvent(initialEvent?.games)
    form.setFieldsValue({
      dateTimeZone: DEFAULT_SPECIAL_EVENT_TIME_ZONE,
      dates: editedDateRange,
      ...initialEvent,
      ...gameFields,
      ...(initialEvent?.notificationsPerGame
        ? {
          notificationsPerGame: restoreDroppedTargeting(
            initialEvent.notificationsPerGame,
            isDraftFor(draftOwnerRef.current, form.getFieldValue("uid"), initialEvent.uid)
              ? form.getFieldValue("notificationsPerGame")
              : undefined,
          ),
        }
        : {}),
    })
    if (importedSpecialEvent && location.state) {
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [editedEvent, form, id, importedSpecialEvent, isLoading, location.pathname, location.state, navigate])

  // scrollToField resolves the field by id, so it can only work once the tab holding it has rendered.
  useEffect(() => {
    if (!pendingScrollRef.current) return
    form.scrollToField(pendingScrollRef.current)
    pendingScrollRef.current = undefined
  }, [gamesTab, form])

  // One Form instance spans :id changes, so nothing may survive into a different campaign — except
  // across this page's own post-save navigate, where the draft is deliberately carried over.
  useEffect(() => {
    // Once saved we are on /<uid>, and the imported payload must not be able to seed a second copy
    // of the same campaign if the admin navigates Back to /new.
    if (id !== "new") setImportedSpecialEvent(undefined)
    if (id && id === draftOwnerRef.current) return
    form.resetFields()
    draftOwnerRef.current = undefined
  }, [id, form])

  // The callback lives on a singleton store, so leaving the page must not arm it for another screen.
  useEffect(() => () => {
    managingJsonStore.resetFuncAfterSuccess()
    cancelWideningRef.current?.()
  }, [managingJsonStore])

  const getStoreData = (): SpecialEventsConfigType | undefined => {
    const eventsData = managingJsonStore?.itemTypes?.filter(
      item => item.type === "specialEvents",
    ) as ConfigItem<"specialEvents">[] | undefined

    return eventsData?.[0]?.data
  }

  const updateStore = async(
    callback: (prev: SpecialEventsConfigType | undefined) => SpecialEventsConfigType,
  ) => {
    const previousData = getStoreData()
    const newData = callback(previousData)

    const item = { name: "specialEvents" as const, type: "specialEvents" as const, data: newData }

    if (previousData) {
      await managingJsonStore.update(item)
    } else {
      await managingJsonStore.create(item)
    }
  }


  // Only this save's own callback may be disarmed: a second click that supersedes the first must not
  // strip the success path off the write already in flight.
  const releaseOwnCallback = () => {
    if (ownCallbackRef.current && managingJsonStore.funcAfterSuccess === ownCallbackRef.current) {
      managingJsonStore.resetFuncAfterSuccess()
    }
    ownCallbackRef.current = undefined
  }

  const endSaving = () => {
    savingRef.current = false
    setIsSaving(false)
  }

  const save = async() => {
    if (!managingJsonStore?.itemTypes) {
      appNotifications.error("Campaign config has not loaded yet. Reload the page and try again.")

      return
    }
    // A ref, not the isSaving state: two clicks in the same tick both read the pre-render value, and
    // rc-field-form supersedes the first validateFields, whose rejection would disarm the second.
    if (savingRef.current) return
    savingRef.current = true
    setIsSaving(true)
    let callbackStarted = false

    try {
      const values = await form.validateFields()

      const {
        games,
        timeBasedGames,
        restrictedToCountries,
        restrictedToRegions,
        dates,
        dateTimeZone,
        notificationsPerGame,
        weight,
        defaultSoundFile,
        ...rest
      } = values
      const selectedTimeZone = dateTimeZone || DEFAULT_SPECIAL_EVENT_TIME_ZONE

      const storeData = getStoreData()
      // editedEvent is still undefined while a post-save refresh is in flight, and minting a fresh uid
      // there appends a second copy of this campaign instead of updating it.
      const campaignUid = editedEvent?.uid
        ?? (id && id !== "new" ? id : undefined)
        ?? draftOwnerRef.current
        ?? uuidv4()
      const storedEvent = storeData?.specialEvents?.find(event => event.uid === campaignUid)
      const neighbours = (storeData?.specialEvents || []).filter(event => event.uid !== campaignUid)

      const newEvent: SpecialEvent = {
        ...rest,
        games: gamesForSave(games, timeBasedGames),
        restrictedToCountries: restrictedToCountries ?? [],
        restrictedToRegions: restrictedToRegions ?? [],
        uid: campaignUid,
        startDate: convertZonedWallTimeToUtc(dates[0], selectedTimeZone).toISOString(),
        endDate: convertZonedWallTimeToUtc(dates[1], selectedTimeZone).toISOString(),
        notificationsPerGame: prepareNotificationsPerGameForSave(
          notificationsPerGame,
          storedEvent?.notificationsPerGame,
        ),
        weight: weight ?? DEFAULT_EVENT_WEIGHT,
        defaultSoundFile: defaultSoundFile,
      }

      const widened = listWidenedGameIds(notificationsPerGame, storedEvent?.notificationsPerGame)
        .map(gameId => getGameLabel(gameId as NotificationsPerGame["gameId"]))

      const confirmed = await confirmWidening(widened, cancel => {
        cancelWideningRef.current = cancel
      })
      cancelWideningRef.current = undefined
      if (!confirmed) return

      draftOwnerRef.current = newEvent.uid
      // The store fires this without awaiting it, so the write resolves while the refresh is still
      // running. Save has to stay disabled across that window — it is the one where editedEvent is
      // not yet repopulated and a second click would mint a fresh uid.
      const onSuccess = async() => {
        callbackStarted = true
        try {
          appNotifications.success("Saved successfully")
          reportTargeting(notificationsPerGame, storedEvent, neighbours, newEvent)
          await managingJsonStore.getData()
          navigate(`/special-events/${newEvent.uid}`)
        } catch (error) {
          // The store fires this without awaiting, so a throw here would surface as an unhandled
          // rejection and the admin would be left on a stale page believing nothing happened.
          appNotifications.error("Saved, but the page could not refresh. Reload to see the current config.")
        } finally {
          endSaving()
        }
      }
      ownCallbackRef.current = onSuccess
      managingJsonStore.funcAfterSuccess = onSuccess

      await updateStore(prev => {
        const prevEvent = prev?.specialEvents?.find(prevEvent => prevEvent.uid === newEvent.uid)
        if (prevEvent) {
          const specialEvents = prev?.specialEvents?.map(prevEvent => {
            if (prevEvent.uid === newEvent.uid) {
              return newEvent
            }

            return prevEvent
          })

          return { specialEvents }
        }

        return { specialEvents: [...(prev?.specialEvents ?? []), newEvent] }
      })

      // Nothing throws on a failed write, so the callback has to be disarmed either way; on success
      // the store has already run it by now.
      releaseOwnCallback()

      return

    } catch (error) {
      releaseOwnCallback()

      // rc-field-form rejects a superseded validateFields with no fields and no Error. Nothing was
      // written, and silence here reads as a save that worked.
      if ((error as { outOfDate?: boolean })?.outOfDate) {
        appNotifications.error("Save was interrupted, try again.")

        return
      }

      const [firstError] = (error as ValidateErrorEntity)?.errorFields || []
      if (!firstError) {
        const reason = error instanceof Error ? error.message : "Unknown error"
        appNotifications.error(`Could not save: ${reason}`)

        return
      }

      if (firstError.name[0] === "notificationsPerGame" && gamesTab !== "events") {
        pendingScrollRef.current = firstError.name
        setGamesTab("events")
      } else {
        form.scrollToField(firstError.name)
      }
      appNotifications.error(
        `Could not save. ${describeField(firstError.name, form.getFieldValue("notificationsPerGame"))}: `
        + firstError.errors.join(" "),
      )

      return
    } finally {
      // The success callback owns the flag once it has started; it clears it after the refresh.
      if (!callbackStarted) endSaving()
    }

  }


  if (isLoading) return (
    <div className={Styles.spinnerWrapper}>
      <Spin size="large" />
    </div>
  )

  return (
    <div className={Styles.container}>
      <SpecialEventEditHeader
        name={editedEvent?.name ?? importedSpecialEvent?.name ?? "New event"}
        onCancel={() => navigate("/special-events")}
        onSave={save}
        isSaving={isSaving}
      />

      <Form form={form} layout="vertical" ref={formRef}>
        <Space direction="vertical" className={Styles.cardContainer} size="large">
          <MainInfoEditor />
          <GamesEditor activeTab={gamesTab} onActiveTabChange={setGamesTab} />
        </Space>
      </Form>
    </div>
  )
})
